import React, { useState, useEffect, useRef } from "react";
import magnifierIcon from "images/magnifier-icon.svg";
import { useNavigationGuard } from "../../../hooks/useNavigationGuard";
import { readSealed, writeSealed, searchPageUrl } from "../../../offline/sealedLocal.js";
import "./SearchBar.css";

const STORAGE_KEY = "recentSearches";
const MAX_RECENT = 8;

// Stored via sealedLocal (encrypted when OFFLINE_ENCRYPTION is on).
function loadRecent() {
  const items = readSealed(STORAGE_KEY, []);
  return Array.isArray(items) ? items : [];
}

function saveRecent(items) {
  writeSealed(STORAGE_KEY, items);
}

const SearchBar = () => {
  const customNavigate = useNavigationGuard();
  const [searchTerm, setSearchTerm] = useState("");
  const [recentSearches, setRecentSearches] = useState(loadRecent);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addToRecent = (term) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    const filtered = recentSearches.filter(
      (s) => s.toLowerCase() !== trimmed.toLowerCase()
    );
    const updated = [trimmed, ...filtered].slice(0, MAX_RECENT);
    setRecentSearches(updated);
    saveRecent(updated);
  };

  const removeFromRecent = (index, e) => {
    e.stopPropagation();
    const updated = recentSearches.filter((_, i) => i !== index);
    setRecentSearches(updated);
    saveRecent(updated);
    if (updated.length === 0) setShowDropdown(false);
  };

  const navigateToSearch = (term) => {
    customNavigate(searchPageUrl(term));
  };

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (highlightIndex >= 0 && highlightIndex < recentSearches.length) {
      const selected = recentSearches[highlightIndex];
      setSearchTerm(selected);
      addToRecent(selected);
      navigateToSearch(selected);
    } else if (searchTerm.trim()) {
      addToRecent(searchTerm);
      navigateToSearch(searchTerm);
    }
    setShowDropdown(false);
    setHighlightIndex(-1);
  };

  const handleSelectRecent = (term) => {
    setSearchTerm(term);
    addToRecent(term);
    navigateToSearch(term);
    setShowDropdown(false);
    setHighlightIndex(-1);
  };

  const handleFocus = () => {
    clearTimeout(blurTimeoutRef.current);
    if (recentSearches.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleBlur = () => {
    // Delay to allow click on dropdown items
    blurTimeoutRef.current = setTimeout(() => setShowDropdown(false), 150);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || recentSearches.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < recentSearches.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : recentSearches.length - 1
      );
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setHighlightIndex(-1);
    }
  };

  const dropdownVisible = showDropdown && recentSearches.length > 0;

  return (
    <div className="search-bar-wrapper" ref={wrapperRef}>
      <form className="search-bar" role="search" onSubmit={handleSearch}>
        <span className="search-icon" aria-hidden="true">
          <img src={magnifierIcon} alt="" />
        </span>
        <input
          type="text"
          className="search-input"
          placeholder="Dokumente durchsuchen..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </form>
      {dropdownVisible && (
        <div className="recent-searches-dropdown">
          <span className="recent-searches-label">Letzte Suchen</span>
          <ul className="recent-searches-list" role="listbox">
            {recentSearches.map((term, i) => (
              <li
                key={term}
                className={`recent-search-item${i === highlightIndex ? " highlighted" : ""}`}
                role="option"
                aria-selected={i === highlightIndex}
                onMouseDown={() => handleSelectRecent(term)}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                <span className="recent-search-text">{term}</span>
                <button
                  className="recent-search-delete"
                  onMouseDown={(e) => removeFromRecent(i, e)}
                  aria-label={`"${term}" entfernen`}
                  type="button"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
